import { useCallback, useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import type { Session } from '../src/types';
import { errorText, getClient, updateClient } from './api';
import type { ClientDetail, ShopChange, StepState, TenDlc } from './api';
import { ago, caption, count, crmLink, day, issuesOf, money, phone, plural, STEPS, toneOfIssue, tradeTown, when } from './format';
import { Strip } from './Strip';
import { Logins } from './Logins';
import { Usage } from './Usage';

export type ClientTab = 'overview' | 'setup' | 'logins' | 'usage' | 'support';
export const CLIENT_TABS: { id: ClientTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'setup', label: 'Setup' },
  { id: 'logins', label: 'Logins' },
  { id: 'usage', label: 'Usage' },
  { id: 'support', label: 'Support' },
];

const tabLink = (id: string, tab: ClientTab) => `#client/${encodeURIComponent(id)}${tab === 'overview' ? '' : `/${tab}`}`;

/** One shop: where setup stands, its settings, logins, usage and LeadsIRL's visits. */
export function ClientPage({ session, id, tab, onChanged }: {
  session: Session;
  id: string;
  tab: ClientTab;
  /** Something changed: the clients list is out of date. */
  onChanged: () => void;
}) {
  const [d, setD] = useState<ClientDetail | null>(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const next = await getClient(session, id);
      setD(next);
      setErr('');
      document.title = `${next.config.businessName} · LeadsIRL Admin`;
    } catch (e) {
      setErr(errorText(e));
    }
  }, [session, id]);
  useEffect(() => {
    setD(null);
    setErr('');
    void load();
  }, [load]);

  /** Make a change, then show the shop as the server now has it. */
  const change = useCallback(async (fn: () => Promise<unknown>) => {
    await fn();
    onChanged();
    await load();
  }, [load, onChanged]);

  if (!d) {
    return (
      <>
        <a className="back" href="#clients">← Clients</a>
        {err ? (
          <div className="banner problem" role="alert">
            <span>Could not load this shop. {err}</span>
            <button type="button" className="ghost" onClick={() => void load()}>Try again</button>
          </div>
        ) : <p className="loading">Loading…</p>}
      </>
    );
  }

  const c = d.config;
  const [tone, text] = caption({ ...c, active: c.active !== false, demo: d.demo, setup: d.setup, attention: d.attention });
  const line = [d.demo ? 'Demo shop' : tradeTown(c), phone(c.phoneNumber)].filter(Boolean).join(' · ');

  return (
    <>
      <a className="back" href="#clients">← Clients</a>
      <div className="top client-top">
        <div className="title">
          <h1>{c.businessName}</h1>
          <p className="summary">{line || 'Setting up'}</p>
        </div>
        <a className="primary" href={crmLink(c.clientId, d.demo)} target="_blank" rel="noopener noreferrer">
          {d.demo ? 'Open the demo CRM' : 'Open their CRM'} <span aria-hidden="true">↗</span>
        </a>
      </div>
      <div className="standing">
        <Strip setup={d.setup} seg={44} />
        <span className={`state ${tone}`}>{text}</span>
      </div>

      <nav className="tabs" aria-label={`${c.businessName}`}>
        {CLIENT_TABS.map((t) => (
          <a key={t.id} href={tabLink(c.clientId, t.id)} className={tab === t.id ? 'on' : ''} aria-current={tab === t.id ? 'page' : undefined}>
            {t.label}
            {t.id === 'logins' && d.logins.length > 0 && <i>{d.logins.length}</i>}
          </a>
        ))}
      </nav>

      {err && <div className="banner problem" role="alert"><span>{err}</span></div>}
      {tab === 'overview' && <Overview d={d} />}
      {tab === 'setup' && <SetupTab key={c.clientId} d={d} save={(ch) => change(() => updateClient(session, c.clientId, ch))} />}
      {tab === 'logins' && <Logins session={session} d={d} change={change} />}
      {tab === 'usage' && <Usage d={d} />}
      {tab === 'support' && <Support d={d} />}
    </>
  );
}

// ------------------------------------------------------------------ Overview

const STEP_TEXT: Record<string, Partial<Record<StepState, string>>> = {
  number: { done: '', todo: 'No business number yet' },
  tenDlc: { done: 'Campaign approved', pending: 'Submitted, waiting on approval', broken: 'Campaign rejected', todo: 'Not submitted yet' },
  logins: { done: 'The owner has signed in', pending: 'Owner invited, has not signed in yet', todo: 'Nobody invited yet' },
  calendar: { done: 'Connected', todo: 'Not connected. The owner connects it in their CRM.' },
  live: { done: '', todo: 'Not live yet' },
};
const STEP_TAB: Record<string, ClientTab> = { number: 'setup', tenDlc: 'setup', logins: 'logins', calendar: 'overview', live: 'setup' };

function Overview({ d }: { d: ClientDetail }) {
  const c = d.config;
  const id = c.clientId;
  const issues = issuesOf(d);
  const lastSignIn = d.logins.map((l) => l.lastActiveAt ?? '').sort().pop();
  const crm = crmLink(id, d.demo);

  const fix = (issue: string): ReactNode => {
    if (issue.startsWith('10DLC')) return <>Fix the campaign in Telnyx, then set its status in <a href={tabLink(id, 'setup')}>Setup</a>.</>;
    if (issue.startsWith('Calendar')) return <>The owner connects it in their CRM: Calendar, then Booking settings. <a href={crm} target="_blank" rel="noopener noreferrer">Open their CRM</a></>;
    if (issue.startsWith('Owner has not')) return <>They may have missed the invite email. <a href={tabLink(id, 'logins')}>Resend it from Logins</a></>;
    if (issue.includes('failed')) return <>See which days in <a href={tabLink(id, 'usage')}>Usage</a>, then the texts in their CRM.</>;
    return null;
  };

  const stepDetail = (key: string, state: StepState): string => {
    if (key === 'number' && state === 'done') return phone(c.phoneNumber);
    if (key === 'live' && state === 'done') return d.demo ? 'Live, used for sales demos' : `Live since ${day(c.liveAt)}`;
    if (key === 'calendar' && state === 'done') {
      const name = c.calendarProvider === 'outlook' ? 'Outlook' : 'Google Calendar';
      return c.calendarConnectedAt ? `${name}, connected ${day(c.calendarConnectedAt)}` : name;
    }
    return STEP_TEXT[key]?.[state] ?? '';
  };

  return (
    <div className="overview">
      {c.active === false && (
        <div className="banner quiet">
          <span><b>Switched off.</b> Calls to their number are hung up, texts to it are dropped, and nobody can text from their CRM.</span>
          <a className="ghost" href={tabLink(id, 'setup')}>Switch on in Setup</a>
        </div>
      )}
      {issues.length > 0 && (
        <section className="card needs">
          <h2>Needs you</h2>
          <ul className="issues">
            {issues.map((i) => (
              <li key={i}>
                <span className={`state ${toneOfIssue(i)}`}>{i}</span>
                <p>{fix(i)}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="tiles">
        <Tile label="Leads, 30 days" value={count(d.leads30)} />
        <Tile label="Texts, 30 days" value={count(d.usage.last30.smsOut + d.usage.last30.smsIn)} />
        <Tile label="Costs you this month" value={money(d.usage.month.smsCostMicros + d.usage.month.aiCostMicros)} />
        <Tile label="Last active in the CRM" value={ago(lastSignIn)} />
      </div>

      <div className="split">
        <section className="card">
          <h2>Setup</h2>
          <ol className="steps">
            {STEPS.map((s) => {
              const state = d.setup[s.key];
              const tab = STEP_TAB[s.key];
              return (
                <li key={s.key} className={state}>
                  <span className="mark" aria-hidden="true" />
                  <div>
                    <b>{s.name}</b>
                    <span>{stepDetail(s.key, state)}</span>
                  </div>
                  {s.key === 'calendar'
                    ? state !== 'done' && <a href={crm} target="_blank" rel="noopener noreferrer">Their CRM</a>
                    : <a href={tabLink(id, tab)}>{state === 'done' ? 'Change' : 'Set up'}</a>}
                </li>
              );
            })}
          </ol>
        </section>

        <section className="card">
          <h2>Details</h2>
          <dl className="facts">
            <dt>Business number</dt><dd>{phone(c.phoneNumber) || <span className="sub">None yet</span>}</dd>
            <dt>Owner's cell</dt><dd>{phone(c.ownerPhone) || <span className="sub">Not set</span>}</dd>
            <dt>Lead alerts to</dt><dd>{c.notifyEmail || <span className="sub">No email</span>}</dd>
            <dt>Time zone</dt><dd>{c.timezone.replace('America/', '').replace(/_/g, ' ')}</dd>
            <dt>Booking</dt><dd>{c.bookingEnabled ? 'The assistant books estimates' : 'Off: the assistant asks them to call'}</dd>
            <dt>Logins</dt><dd>{d.logins.length ? plural(d.logins.length, 'login') : <span className="sub">None yet</span>}</dd>
            <dt>Added</dt><dd>{day(c.createdAt) || <span className="sub">Unknown</span>}</dd>
            <dt>Shop id</dt><dd><code>{id}</code></dd>
          </dl>
          <p className="fine">The owner changes the assistant, hours and booking in their own CRM. You can too: <a href={crm} target="_blank" rel="noopener noreferrer">open their CRM</a>.</p>
        </section>
      </div>
    </div>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="tile">
      <span>{label}</span>
      <b>{value}</b>
    </div>
  );
}

// --------------------------------------------------------------------- Setup

const TEN_DLC: { id: TenDlc; label: string }[] = [
  { id: 'not-started', label: 'Not started' },
  { id: 'submitted', label: 'Submitted' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
];
const TRADES = ['Roofing', 'Remodeling', 'Painting', 'Concrete', 'Gutters', 'Siding', 'Windows', 'Plumbing', 'HVAC', 'Electrical', 'Landscaping', 'Flooring', 'Decks', 'Handyman'];

function SetupTab({ d, save }: { d: ClientDetail; save: (change: ShopChange) => Promise<void> }) {
  const c = d.config;
  const start = {
    businessName: c.businessName,
    industry: c.industry ?? '',
    city: c.city ?? '',
    phoneNumber: phone(c.phoneNumber),
    tenDlcStatus: (c.tenDlcStatus ?? 'not-started') as TenDlc,
  };
  const [form, setForm] = useState(start);
  // Saved (the number comes back as +1...), or changed elsewhere: show what the server now has.
  const [saves, setSaves] = useState(0);
  const startKey = JSON.stringify(start);
  useEffect(() => setForm(JSON.parse(startKey) as typeof start), [startKey, saves]);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const changed = (Object.keys(start) as (keyof typeof start)[]).filter((k) => form[k].trim() !== start[k].trim());
  const set = (k: keyof typeof start, v: string) => { setForm({ ...form, [k]: v }); setNote(''); };

  const run = async (what: string, change: ShopChange, done: string) => {
    setBusy(what);
    setErr('');
    setNote('');
    try {
      await save(change);
      // Show the saved values; Status buttons leave unsaved edits above alone.
      if (what === 'save') setSaves((n) => n + 1);
      setNote(done);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy('');
    }
  };
  const onSave = (e: FormEvent) => {
    e.preventDefault();
    if (!form.businessName.trim()) { setErr('The business name cannot be empty.'); return; }
    const change: ShopChange = {};
    for (const k of changed) (change as Record<string, string>)[k] = form[k].trim();
    void run('save', change, 'Saved.');
  };
  const live = Boolean(c.liveAt);
  const on = c.active !== false;
  const switchOff = () => {
    if (!window.confirm(`Switch off ${c.businessName}?\n\nUntil you switch it back on, calls to their number are hung up (not put through to the owner), texts to it are dropped, and nobody can text from their CRM. They can still sign in and see their leads.`)) return;
    void run('active', { active: false }, 'Switched off.');
  };

  return (
    <div className="setup">
      <form className="card" onSubmit={onSave}>
        <h2>Business</h2>
        <div className="fields">
          <label>Business name<input value={form.businessName} onChange={(e) => set('businessName', e.target.value)} maxLength={80} /></label>
          <label>Trade<input value={form.industry} onChange={(e) => set('industry', e.target.value)} list="trades" maxLength={60} placeholder="Roofing" /></label>
          <label>Town<input value={form.city} onChange={(e) => set('city', e.target.value)} maxLength={60} placeholder="Novi" /></label>
        </div>
        <datalist id="trades">{TRADES.map((t) => <option key={t} value={t} />)}</datalist>

        <h2>Texting</h2>
        <div className="fields">
          <label>
            Business number
            <input value={form.phoneNumber} onChange={(e) => set('phoneNumber', e.target.value)} inputMode="tel" placeholder="(248) 555-0100" />
            <small>The Telnyx number customers call. Missed calls to it are texted from the moment it's saved.</small>
            {form.phoneNumber.trim() && !start.phoneNumber && form.tenDlcStatus !== 'approved' && (
              <small className="warn">Their 10DLC campaign isn't approved yet. Texts from an unregistered number get blocked by the carriers.</small>
            )}
          </label>
        </div>
        <div className="field">
          <span className="label" id="tendlc">10DLC campaign</span>
          <div className="segmented" role="radiogroup" aria-labelledby="tendlc">
            {TEN_DLC.map((t) => (
              <button key={t.id} type="button" role="radio" aria-checked={form.tenDlcStatus === t.id} className={form.tenDlcStatus === t.id ? 'on' : ''} onClick={() => set('tenDlcStatus', t.id)}>
                {t.label}
              </button>
            ))}
          </div>
          <small>Each contractor needs a 10DLC campaign of their own, filed in Telnyx. Never put a contractor on LeadsIRL's campaign (CBU44JT).</small>
        </div>

        {err && <p className="err" role="alert">{err}</p>}
        <div className="actions">
          <button className="primary" type="submit" disabled={!changed.length || busy !== ''}>{busy === 'save' ? 'Saving…' : 'Save changes'}</button>
          {changed.length > 0 && <button type="button" className="ghost" onClick={() => { setForm(start); setErr(''); }}>Undo changes</button>}
          {note && !changed.length && <span className="saved" role="status">{note}</span>}
        </div>
      </form>

      <section className="card">
        <h2>Status</h2>
        <div className="switch-row">
          <div>
            <b>{live ? (d.demo ? 'Live, used for sales demos' : `Live since ${day(c.liveAt)}`) : 'Not live yet'}</b>
            <p>Live means setup is finished and they're using it. It doesn't change what the assistant does.</p>
          </div>
          <button type="button" className="ghost" disabled={busy !== ''} onClick={() => void run('live', { live: !live }, live ? 'Marked not live.' : 'Marked live.')}>
            {busy === 'live' ? 'Saving…' : live ? 'Mark as not live' : 'Mark as live'}
          </button>
        </div>
        <div className="switch-row">
          <div>
            <b>{on ? 'Switched on' : 'Switched off'}</b>
            <p>{on ? 'Calls and texts to their number are answered as set up.' : 'Calls to their number are hung up, texts to it are dropped, and nobody can text from their CRM. They can still sign in and see their leads.'}</p>
          </div>
          {on
            ? <button type="button" className="danger" disabled={busy !== ''} onClick={switchOff}>{busy === 'active' ? 'Saving…' : 'Switch off'}</button>
            : <button type="button" className="primary" disabled={busy !== ''} onClick={() => void run('active', { active: true }, 'Switched on.')}>{busy === 'active' ? 'Saving…' : 'Switch on'}</button>}
        </div>
      </section>
    </div>
  );
}

// ------------------------------------------------------------------- Support

function Support({ d }: { d: ClientDetail }) {
  return (
    <section className="card">
      <h2>Support log</h2>
      <p className="fine">Every time someone at LeadsIRL opens {d.config.businessName}'s CRM, it is noted here: once an hour per person, kept for 400 days.</p>
      {d.support.length === 0 ? (
        <p className="empty">Nobody from LeadsIRL has opened their CRM yet.</p>
      ) : (
        <table className="plain">
          <thead><tr><th>When</th><th>Who</th></tr></thead>
          <tbody>
            {d.support.map((v) => (
              <tr key={`${v.hour}|${v.email}`}>
                <td>{when(v.lastAt || `${v.hour}:00:00Z`)}</td>
                <td>{v.email}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
